<template>
  <div class="p-grid">
    <div class="p-col-12">
      <div class="card">
        <BillingFeatureGate feature="purchase_invoices"
                            variant="page"
                            :title="$t('Purchase invoices')"
                            :description="$t('billing.features.purchase_invoices.tooltip')"
                            icon="ti-receipt">
          <Toolbar class="p-mb-4">
            <template #start>
              <div style="font-size: 1.4em; color: inherit" class="p-mr-3">{{ $t('Purchase invoices') }}</div>
              <AddNewButton v-show="!isFocusMode" :loading="!allDataLoaded" :disabled="!allDataLoaded" :label="$t('Add')" @click="addNewItem"/>
            </template>
            <template #end>
              <Button v-if="isFocusMode"
                      :icon="'pi pi-bars'"
                      :label="$t('Show all')"
                      @click="exitFocusedMode"
                      class="p-button-raised"/>
              <template v-else>
                <div class="p-d-flex p-ai-center p-mr-2"
                     v-if="$store.getters.isAccountantOrHigher">
                  <Checkbox :disabled="dataTableIsLoading" @change="getItems(true)" id="purchase-invoices-show-only-unpaid-inputswitch" :binary="true" v-model="showOnlyUnpaid"/>
                  <div>
                    <label for="purchase-invoices-show-only-unpaid-inputswitch">
                      <div class="pointer p-p-1">{{ $t('showOnlyUnpaidLabel') }}</div>
                    </label>
                  </div>
                </div>
                <CustomDropdown id="supplier"
                                class="p-mr-2"
                                style="min-width: 180px; max-width: 200px;"
                                data-key="id"
                                ref="selectedSupplierDropdown"
                                :searchData="supplierSearchData"
                                v-model="selectedSupplier"
                                :options="suppliersData"
                                :disabled="dataTableIsLoading"
                                :filterPlaceholder="$t('lineItemSearchSupplierPlaceholder')"
                                optionLabel="company_name"
                                :filter="true"
                                :filterFields="['name', 'company_name', 'reg_number']"
                                :loading="suppliersDataIsLoading"
                                @change="changeSupplier"
                                @filter="searchSuppliers"
                                :showClear="true">
                  <template #value="slotProps">
                    <div v-if="slotProps.value">
                      <div>{{ slotProps.value.company_name }} <span v-if="slotProps.value.name">({{ slotProps.value.name }})</span></div>
                    </div>
                    <span v-else>{{ $t('lineItemSelectSupplierPlaceholder') }}</span>
                  </template>
                  <template #option="slotProps">
                    <div v-if="slotProps.option">
                      <div>{{ slotProps.option.company_name }} <span v-if="slotProps.option.name">({{ slotProps.option.name }})</span></div>
                    </div>
                  </template>
                </CustomDropdown>
                <DatesRangeCalendar :disabled="dataTableIsLoading"
                                    ref="datesRangeCalendar"
                                    :isEmptyByDefault="true"
                                    @change-dates-range="changeDatesRange"
                                    class="p-mr-2"/>
                <SearchInput :disabled="dataTableIsLoading"
                             @search-items="getItems"
                             @change-processed-search-value="changeProcessedSearchValue"
                             v-model="searchData"
                             @show-spinner="isLoading = true"/>
              </template>
            </template>
          </Toolbar>

          <Toolbar class="p-mb-4" v-if="showInvoicesSidebar && selectedInvoicesData?.length">
            <template #start>
              <div style="line-height: 1.5rem">
                <div class="p-mb-2">
                  <a href="#" draggable="false" @click.prevent="toggleShowDetailedInfo">
                    <i style="font-size: 0.9em" class="p-mr-1 pi" :class="showDetailedInfo ? 'pi-minus' : 'pi-plus'"/>
                    <span>
                      {{ showDetailedInfo ? $t('hideDetailedInfoLabel') : $t('showDetailedInfoLabel') }}
                    </span>
                  </a>
                </div>
                <div v-show="showDetailedInfo">
                  <div v-for="(data, index) of selectedInvoicesData" :key="index" class="">
                    <b>{{ $t('Supplier') }}: </b>{{ data.company_name }}
                    <div v-if="data.bank_name || data.bank_account">
                      <b>{{ $t('bankInfoLabel') }}: </b>
                      <span>{{ [data.bank_name, data.bank_account].filter(Boolean).join(', ') }}</span>
                    </div>
                    <div>
                      <b>{{ $t('Invoices') }}: </b>
                      <span v-for="(invoice, index) of data.selectedInvoices" :key="index">
                        {{ invoice }}<span v-if="index !== data.selectedInvoices.length - 1">, </span>
                      </span>
                    </div>
                    <div>
                      <b>{{ $t('Sum') }}: </b>{{ formatCurrency(data.totalSum) }}
                    </div>
                    <hr style="margin: 8px 0"/>
                  </div>
                </div>
                <div class="p-mb-2">
                  <div>{{ $t('selectedCountLabel') }}: {{ selectedItems?.length }}</div>
                  <div>{{ $t('Total sum') }}:  {{ formatCurrency(totalSelectedInvoicesSum) }}</div>
                </div>
                <div>
                  <Button class="p-button-raised p-button-secondary p-mt-1 p-mr-2"
                          :label="$t('clearSelectionLabel')"
                          @click="unselectAllItems(true)"/>
                  <span class="p-d-inline-flex"
                        v-tooltip.top="billingActionTooltipOptions()">
                    <Button class="p-button-raised p-mt-1"
                            :label="$t('markAsPaidLabel')"
                            :disabled="billingDataEditingLocked"
                            @click="changeStatusOnClick"/>
                  </span>
                </div>
              </div>
            </template>
          </Toolbar>

          <Spinner v-if="isLoading"/>
          <DesktopDataTable v-else
                            :tableName="$t('Purchase invoices')"
                            :dataTableIsLoading="dataTableIsLoading"
                            :showHeader="false"
                            :itemsArrayIsEmpty="itemsArrayIsEmpty"
                            :sort="sort"
                            :showSearchInput="false"
                            :actionButtonsCount="actionButtonsCount"
                            @sort-items="sortItems"
                            :headers="[
                  {name: 'number', title: $t('Number'), sortable: true},
                  {name: 'export_number', icon: 'ti-link', tooltip: $t('accountingExportColumnTooltip'), sortable: false, isHidden: !showAccountingExportColumn},
                  {name: 'supplier', title: $t('Supplier'), sortable: false},
                  {name: 'issued', title: $t('issuedLabel'), sortable: true},
                  {name: 'payment_due', title: $t('paymentDueLabel'), sortable: false},
                  {name: 'overdue', title: '', sortable: false},
                  {name: 'grand_total', title: $t('Sum'), sortable: false},
                  {name: 'unpaid', title: $t('Due'), sortable: false},
                  {name: 'state', title: $t('Status'), sortable: false},
                  {name: 'created_at', title: $t('Created'), sortable: true}
              ]">
            <template v-slot:multiselectHeader v-if="!isFocusMode">
              <th v-show="thereIsUnpaidInvoice && $store.getters.isAccountantOrHigher" class="multiSelect-column-header">
                <Checkbox @click="selectAllCheckboxOnClick" :value="0" v-model="selectAllCheckbox"/>
              </th>
            </template>
            <template v-slot:body>
              <tr v-for="(item, index) of items"
                  :key="index"
                  :class="{'p-highlight': isItemSelected(item.id), 'updated-item': isItemUpdated(item.id)}">
                <td v-show="!isFocusMode && thereIsUnpaidInvoice && $store.getters.isAccountantOrHigher">
                  <Checkbox v-if="item.state === 1" :value="item.id" v-model="selectedItems"/>
                </td>
                <td>
                  <span v-if="item.number">{{ item.number }}</span>
                  <i v-else class="ti-minus"/>
                </td>
                <td v-if="showAccountingExportColumn">
<!--                  <div v-if="item.export_number || item.export_id" >-->
<!--                    <i class="ti-link" v-tooltip.top="item.export_number"/>-->
<!--                    <ChangedAfterExportIcon :show="item.changed_after_export" size="1.05rem" top="-1px"/>-->
<!--                  </div>-->
                  <div v-if="item.export_number || item.export_id">
                    <i class="ti-link" v-tooltip.top="getAccountingExportTooltip(item)"/>
                    <ChangedAfterExportIcon :show="item.changed_after_export" size="1.05rem" top="-1px"/>
                  </div>
                  <i v-else class="ti-minus"/>
                </td>
                <td>
                  <span v-if="item.supplier">{{ item.supplier.company_name }}</span>
                  <i v-else class="ti-minus"/>
                </td>
                <td>
                  <span v-if="item.issued">{{ formatDay(item.issued) }}</span>
                  <i v-else class="ti-minus"/>
                </td>
                <td>
<!--                  :class="{'warning-color': item.payment_due && (countOverdueDays(item.payment_due) < 0) && item.state !== 2 && item.state !== 5 }">-->
                  <span v-if="item.payment_due"
                        :class="paymentDueClass(item)">
                    {{ formatDay(item.payment_due) }}
                  </span>
                  <i v-else class="ti-minus"/>
                </td>
                <td>
<!--                  :class="{'warning-color': countOverdueDays(item.payment_due) < 0, 'orange-color': countOverdueDays(item.payment_due) === 0 }">-->
                  <span v-if="item.payment_due && item.state !== 2 && item.state !== 5"
                        :class="paymentDueClass(item)">
                    {{ countOverdueDays(item.payment_due) }}
                  </span>
                </td>
                <td>
                  <span>{{ formatCurrency(item.grand_total) }}</span>
                </td>
                <td>
                  <span>{{ formatCurrency(item.unpaid) }}</span>
                </td>
                <td>
                  <div  class="p-d-flex p-flex-wrap p-ai-center">
                    <div class="state-badge" :class="PurchaseInvoiceStates.find(state => state.num === item.state)?.color || ''">{{ PurchaseInvoiceStates.find(state => state.num === item.state)?.label[$i18n.locale] || '' }}</div>
                  </div>
                </td>

                <td class="table-date-line">
                  <span v-if="item.created_at">
                    {{ formatBy(item.created_at, item.creator) }}
                  </span>
                  <i v-else class="ti-minus"/>
                </td>

                <td v-show="actionButtonsCount">
                  <div class="actions">
                    <EditButton v-show="canManage"
                                :disabled="!showModals || disableEditButton || !allDataLoaded || dataTableIsLoading"
                                @click="editItem(item)"/>
                    <span v-show="canManage"
                          v-tooltip.left="{
                            value: $t('financialPeriodClosedTooltip'),
                            disabled: canDeleteInvoice(item) || billingDataEditingLocked
                          }">
                      <DeleteButton @click="confirmItemDelete(item)"
                                    :disabled="!showModals || dataTableIsLoading || !canDeleteInvoice(item)"/>
                    </span>
                  </div>
                </td>
              </tr>
            </template>
          </DesktopDataTable>

          <OwnPaginator v-show="!itemsArrayIsEmpty"
                        :rows="pagination.perPage"
                        :currentPage="pagination.currentPage - 1"
                        :totalPages="pagination.pageCount"
                        :totalRecords="pagination.totalCount"
                        :rowsPerPageOptions="[5,10,25,50,100,200]"
                        :pageLinkSize="11"/>

          <TotalRecordsData v-show="!dataTableIsLoading" :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>

          <PurchaseInvoiceModal v-if="showModals"
                                :visible="itemModal"
                                :item="item"
                                :taxes="taxes"
                                :suppliers="suppliers"
                                :warehouseItems="warehouseItems"
                                @update-items="updateItems"
                                @close="closeItemModal"/>

          <ConfirmDeleteModal v-if="showModals"
                              :modalDataIsLoading="modalDataIsLoading"
                              :visible="confirmItemDeleteModal"
                              @close="closeConfirmItemDeleteModal"
                              @delete-item="deleteItem"
                              :data="itemToDeleteData"/>

          <ConfirmModal v-if="showModals"
                        :visible="changeStatusModal"
                        :modalDataIsLoading="modalDataIsLoading"
                        @confirm="changeStateToPaid"
                        @close="closeConfirmChangeStatusModal"/>
        </BillingFeatureGate>
      </div>
    </div>
  </div>
</template>

<script>
import httpClient from '@/services/http.services'
import PurchaseInvoiceModal from '@/pages/finance/components/PurchaseInvoiceModal'
import PurchaseInvoiceStates from '@/translations/states/PurchaseInvoiceStates'
import ConfirmModal from '@/components/ConfirmModal'
import formatMixins from '@/mixins/formatMixins'
import calculateMixins from '@/mixins/calculateMixins'
import httpMixins from '@/mixins/httpMixins'
import datatableMixins from '@/mixins/datatableMixins'
import settings from "@/settings";
import DatesRangeCalendar from "@/components/DatesRangeCalendar";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import ChangedAfterExportIcon from "@/components/ChangedAfterExportIcon.vue";
import BillingFeatureGate from '@/components/billing/BillingFeatureGate.vue'
import billingFeaturesMixins from '@/mixins/billingFeaturesMixins'
import billingRestrictionsMixin from '@/mixins/billingRestrictionsMixin'

export default {
  mixins: [
    formatMixins,
    calculateMixins,
    httpMixins,
    datatableMixins,
    showErrorsMixins,
    billingFeaturesMixins,
    billingRestrictionsMixin
  ],
  components: {
    PurchaseInvoiceModal,
    ConfirmModal,
    DatesRangeCalendar,
    ChangedAfterExportIcon,
    BillingFeatureGate
  },
  data() {
    return {
      showDetailedInfo: false,
      showOnlyUnpaid: false,
      changeStatusModal: false,
      showInvoicesSidebar: false,
      taxes: null,
      suppliers: null,
      warehouseItems: [],
      // isLoading: true,
      providerId: null,
      fieldsExpand: 'accounting_provider_name,supplier,creator,positions,positions.tax,positions.creator,positions.order',
      apiLink: 'purchase-invoice',
      deleteItemUrl: 'purchase-invoice/delete',
      routePath: '/purchase-invoices',
      // searchData: '',
      sort: {
        column: 'issued',
        direction: '-',
      },
      selectedSuppliers: [],
      selectedInvoices: [],
      selectedInvoicesData: [],
      thereIsUnpaidInvoice: null,
      totalSelectedInvoicesSum: 0,
      PurchaseInvoiceStates,
    }
  },
  watch: {
    itemModal(b) {
      if (b) {
        this.checkModalDataIsFresh()
      }
    },
    suppliers(value) {
      if (value?.length) {
        this.suppliersData = [...value]
      } else {
        this.suppliersData = null
      }
    },
    selectedItems: {
      handler() {
        this.checkAllItemsAreSelected()
        this.toggleInvoicesSidebar()
        if (!this.selectedItems?.length) {
          this.showDetailedInfo = false
        }
      },
      deep: true
    },
    '$store.state.firstLayerIsOpened'() {
      if (!this.$store.state.firstLayerIsOpened) {
        if (this.changeStatusModal) {
          this.closeConfirmChangeStatusModal()
        }
      }
    },
    canUsePurchaseInvoices(allowed, wasAllowed) {
      if (allowed && !wasAllowed) {
        this.$nextTick(() => {
          this.checkRouteQuery()
          this.getPageData()
        })
        return
      }

      if (!allowed && wasAllowed) {
        this.resetPurchaseInvoicesState()
      }
    },
  },
  mounted() {
    this.scrollToTop()
    if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) return false
    // this.checkRouteQuery()
    // this.getPageData()
    if (this.canUsePurchaseInvoices) {
      this.checkRouteQuery()
      this.getPageData()
    }
  },
  methods: {
    getPageData() {
      if(!this.canUsePurchaseInvoices) return
      if (!this.userCanGetData || this.fetchInFlight) return
      this.getItems()

      this.getModalDataOnMounted().then(() => {
        if (this.canUsePurchaseInvoices) {
          this.checkSupplierInArray()
        }
      })
    },
    paymentDueClass(item) {
      if (!item.payment_due || item.state === 2 || item.state === 5 || Number(item.unpaid) <= 0) {
        return ''
      }

      const days = this.countOverdueDays(item.payment_due)

      if (days < 0) return 'warning-color'
      if (days === 0) return 'orange-color'

      return ''
    },
    checkFinancialPeriodHasBeenClosed(issued) {
      const issuedTs = Number(issued || 0)
      const lockTs = Number(this.$store.getters.locationData?.financial_period_locked_until || 0)

      return issuedTs > 0 && lockTs > 0 && issuedTs <= lockTs
    },

    resetPurchaseInvoicesState() {
      this._abortors?.purchaseInvoices?.abort?.()

      this.items = []
      this.selectedItems = []
      this.selectAllCheckbox = []

      this.selectedInvoices = []
      this.selectedInvoicesData = []
      this.selectedSuppliers = []
      this.totalSelectedInvoicesSum = 0

      this.showInvoicesSidebar = false
      this.showDetailedInfo = false

      this.taxes = null
      this.suppliers = null
      this.warehouseItems = []

      this.itemModal = false
      this.confirmItemDeleteModal = false
      this.changeStatusModal = false

      this.isLoading = false
      this.dataTableIsLoading = false

      this.thereIsUnpaidInvoice = null
      this.selectedSupplier = null
      this.selectedSupplierId = null

      this.item = {}
      this.modalDataIsLoading = false
      this.disableEditButton = false
    },

    canDeleteInvoice(invoice) {
      if (!invoice?.id) return false
      if (!this.$store.getters.isAccountantOrHigher) return false
      if (!this.allDataLoaded) return false
      if (this.dataIsSending) return false

      if (this.checkFinancialPeriodHasBeenClosed(invoice.issued)) {
        return false
      }

      return true
    },
    toggleShowDetailedInfo() {
      this.showDetailedInfo = !this.showDetailedInfo
    },
    toggleInvoicesSidebar() {
      this.showInvoicesSidebar = !!this.selectedItems?.length
      this.createSelectedInvoicesData()
    },
    changeStatusOnClick() {
      if (this.billingActionIsRestricted()) return

      this.changeStatusModal = true
      this.$store.commit('toggleFirstLayer', true)
    },
    closeConfirmChangeStatusModal() {
      this.changeStatusModal = false
      this.$store.commit('toggleFirstLayer', false)
    },
    async changeStateToPaid() {
      if (this.billingActionIsRestricted()) {
        this.closeConfirmChangeStatusModal()
        return
      }

      if (!this.selectedItems?.length) return
      this.disableEditButton = true
      this.updatedItemId = null
      this.dataTableIsLoading = true
      this.modalDataIsLoading = true
      try {
        const { status, data } = await httpClient.post('purchase-invoice/multiple-change-state', { ids: this.selectedItems })
        if (status === 200 && data?.success) {
          this.$toast.add({severity:'success', detail: this.$t('Data updated'), life: this.settings.toastLife});
          this.updatedItemsIds = [ ...this.selectedItems ]
          this.selectedItems = []
          this.getItems()

          this.closeConfirmChangeStatusModal()

          this.selectedItems = []
          this.selectAllCheckbox = []

          this.clearSelectedInvoicesData()
        } else {
          this.showUnknownError()
        }
      } catch(err) {
        this.showError(err)
      } finally {
        this.disableEditButton = false
        this.dataTableIsLoading = false
        this.modalDataIsLoading = false
      }
    },
    clearSelectedInvoicesData() {
      this.selectedInvoicesData = []
      this.selectedSuppliers = []
    },
    createSelectedInvoicesData() {
      if (this.selectedItems?.length) {
        this.clearSelectedInvoicesData()
        this.selectedItems.forEach(itemId => {
          const invoice = this.selectedInvoices.find(i => i.id === itemId)
          if (!invoice) {
            const newInvoice = this.items.find(i => i.id === itemId)
            if (newInvoice) {
              this.selectedInvoices.push(newInvoice)
            }
          }
        })

        // let totalSelectedInvoicesSum = 0
        this.selectedInvoices.forEach(i => {
          // totalSelectedInvoicesSum += +i.grand_total
          const index = this.selectedItems.indexOf(i.id)
          if (index === - 1) {
            this.selectedInvoices = this.selectedInvoices?.filter(invoice => invoice.id !== i.id)
          }
        })

        this.selectedInvoices.forEach(invoice => {
          if (invoice.supplier && invoice.supplier.company_name) {
            const supplier = this.selectedSuppliers.find(supplier => supplier.id === invoice.supplier_id)
            if (!supplier) {
              this.selectedSuppliers.push({ company_name: invoice.supplier.company_name, bank_name: invoice.supplier.bank_name, bank_account: invoice.supplier.bank_account, id: invoice.supplier_id })
            }
          }
        })

        this.totalSelectedInvoicesSum = this.selectedInvoices.reduce((sum, item) => sum + +item.grand_total, 0)

        this.selectedSuppliers?.forEach(supplier => {
          const selectedInvoices = this.selectedInvoices.filter(invoice => invoice.supplier_id === supplier.id)

          this.selectedInvoicesData.push({
            company_name: supplier.company_name,
            bank_name: supplier.bank_name,
            bank_account: supplier.bank_account,
            id: supplier.id,
            selectedInvoices: selectedInvoices?.map(inv => inv.number),
            totalSum: selectedInvoices?.reduce((sum, item) => sum + +item.grand_total, 0)
          })
        })
      }
    },
    selectAllItems() { // Don't delete
      if (!this.items?.length) return false
      this.items.filter(item => item.state === 1 && !this.isItemSelected(item.id)).forEach(item => this.selectedItems.push(item.id))
    },
    checkAllItemsAreSelected() { //Don't delete
      if (!this.items?.length) return false
      let allItemsAreSelected = true
      this.items.filter(item => item.state === 1).forEach(item => {
        if (!this.isItemSelected(item.id)) {
          allItemsAreSelected = false
        }
      })

      if (allItemsAreSelected && this.selectedItems?.length) {
        this.selectAllCheckbox = [0]
      } else {
        this.selectAllCheckbox = []
      }
    },
    async getItems(showSpinner = false) {
      if (!this.canUsePurchaseInvoices) return
      if (!this.userCanGetData || this.fetchInFlight) return
      if (showSpinner || this.itemNotFound) this.isLoading = true
      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true
      const ctrl = this.setController('purchaseInvoices')
      try {
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          method: 'post',
          params: {
            supplierId: this.isFocusMode ? null : this.selectedSupplierId,
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.isFocusMode ? null : (this.processedSearchValue ?? null),
            expand: this.fieldsExpand,
            show_only_unpaid: this.isFocusMode ? 0 : +this.showOnlyUnpaid,
            show: this.$route.query.show || null,
          },
          data: {
            dateFrom: this.fromTimestamp,
            dateTo: this.toTimestamp,
          },
          signal: ctrl?.signal,
        })

        if (!this.canUsePurchaseInvoices) return

        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }

          this.items = data
          this.thereIsUnpaidInvoice = data.find(item => item.state === 1)

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }

          if (this.$route.query.show && (data.length === 0)) {
            this.showItemNotFoundError()
            this.itemNotFound = true
            return
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch(err) {
        this.showError?.(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.dataTableIsLoading = false
        this.disableEditButton = false
        this.checkAllItemsAreSelected()
        if (this._abortors?.purchaseInvoices === ctrl) {
          this._abortors.purchaseInvoices = null
        }
      }
    },
    closeItemModal() {
      this.itemModal = false
      if (this.$store.state.firstLayerIsOpened === true ) {
        this.$store.commit('toggleFirstLayer', false)
      }
      this.item = {}
      this.submitted = false
    },
    addNewItem() {
      if (this.billingActionIsRestricted()) return

      this.item = { isNew: true }
      this.submitted = false
      this.itemModal = true
      this.$store.commit('toggleFirstLayer', true)
    },
    editItem(item) {
      if (this.billingActionIsRestricted()) return

      this.item = item

      if (item.order_consumable) {
        this.item.use_consumables = true
      } else {
        this.item.use_consumables = false
      }


      this.itemModal = true
      this.$store.commit('toggleFirstLayer', true)
    },
    confirmItemDelete(itemToDelete) {
      if (this.billingActionIsRestricted()) return
      if (!itemToDelete) return

      this.itemToDelete = { ...itemToDelete }
      this.confirmItemDeleteModal = true
      this.$store.commit('toggleFirstLayer', true)
    },
    async deleteItem() {
      if (this.billingActionIsRestricted()) {
        this.closeConfirmItemDeleteModal()
        return
      }

      return datatableMixins.methods.deleteItem.call(this)
    },
  },
  computed: {
    allDataLoaded() {
      return !!(this.taxes && this.suppliers && this.warehouseItems)
    },
    canManage() {
      return this.$store.getters.isAccountantOrHigher
    },
    actionButtonsCount() {
      return this.canManage ? 2 : 0
    },
    itemToDeleteData() {
      return  this.itemToDelete?.number ?? null
    },
  }
}
</script>

<style scoped lang="scss">
.selected-invoices-sidebar {
  font-weight: normal;
}


td {
  height: 44px
}
</style>